import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, FormControl, Select, MenuItem, InputLabel, useTheme, useMediaQuery, TablePagination } from '@mui/material';
import { Icon } from '@iconify/react';
import { getAuditLogsByUserId, AuditLogEntry } from '../utils/auditLog';
interface AuditLogDrawerProps {
  open: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
}
const AuditLogDrawer: React.FC<AuditLogDrawerProps> = ({
  open,
  onClose,
  userId,
  userName
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Load audit logs when drawer opens or userId changes
  useEffect(() => {
    if (open && userId) {
      const logs = getAuditLogsByUserId(userId);
      setAuditLogs(logs);
    }
  }, [open, userId]);
  // Filter logs
  const filteredLogs = auditLogs.filter(log => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!log.fieldName.toLowerCase().includes(query) && !log.oldValue.toLowerCase().includes(query) && !log.newValue.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Event type filter
    if (eventTypeFilter !== 'all' && log.eventType !== eventTypeFilter) {
      return false;
    }
    // Source filter
    if (sourceFilter !== 'all' && log.source !== sourceFilter) {
      return false;
    }
    return true;
  });
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'Create':
        return {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        };
      case 'Edit':
        return {
          bg: 'rgba(14, 165, 233, 0.1)',
          color: '#0EA5E9'
        };
      case 'Sync':
        return {
          bg: 'rgba(139, 92, 246, 0.1)',
          color: '#8B5CF6'
        };
      case 'Status Change':
        return {
          bg: 'rgba(245, 158, 11, 0.1)',
          color: '#F59E0B'
        };
      default:
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
    }
  };
  const getSourceColor = (source: string) => {
    switch (source) {
      case 'BO_USER':
        return {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        };
      case 'BANK_API':
        return {
          bg: 'rgba(6, 182, 212, 0.1)',
          color: '#06B6D4'
        };
      case 'SYSTEM':
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
      default:
        return {
          bg: 'rgba(100, 116, 139, 0.1)',
          color: '#64748B'
        };
    }
  };
  // Paginated logs
  const paginatedLogs = filteredLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        md: 900
      },
      p: 0
    }
  }} data-id="element-131" data-mp="1t7evi9s04sd6">
      <Box sx={{
      p: 3,
      borderBottom: '1px solid #E5E7EB'
    }} data-id="element-132" data-mp="2vqqbexxccti">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-133" data-mp="7utxb4xgst44">
          <Box data-id="element-134" data-mp="o42q19sr24op">
            <Typography variant="h6" data-id="element-135" data-mp="jb9p6x0ejghw">Audit Log</Typography>
            {userName && <Typography variant="body2" color="text.secondary" data-id="element-136" data-mp="j9ezz6i3l280">
                {userName} - {filteredLogs.length} entries
              </Typography>}
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-137" data-mp="9ewvhz4ux2n5">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-138" data-mp="1713zb6tc6qyg" />
          </IconButton>
        </Box>
        {/* Filters */}
        <Box sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        mt: 2
      }} data-id="element-139" data-mp="1232hep7ncxok">
          <TextField size="small" placeholder="Search field, old value, new value..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{
          flex: 1,
          minWidth: 200
        }} InputProps={{
          startAdornment: <InputAdornment position="start" data-id="element-141" data-mp="1sd2up4hdfukm">
                  <Icon icon="lucide:search" width={16} height={16} data-id="element-142" data-mp="1xaupc69ehusi" />
                </InputAdornment>
        }} data-id="element-140" data-mp="s3leugnoyzwt" />
          <FormControl size="small" sx={{
          minWidth: 150
        }} data-id="element-143" data-mp="228wffb58uudh">
            <InputLabel data-id="element-144" data-mp="271z1ky7oz1jn">Event Type</InputLabel>
            <Select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)} label="Event Type" data-id="element-145" data-mp="2c0ac7b5t9g2x">
              <MenuItem value="all" data-id="element-146" data-mp="2g371tltrqcgd">All Types</MenuItem>
              <MenuItem value="Create" data-id="element-147" data-mp="2l1qo5nc9xq99">Create</MenuItem>
              <MenuItem value="Edit" data-id="element-148" data-mp="2pzigujkqtgei">Edit</MenuItem>
              <MenuItem value="Sync" data-id="element-149" data-mp="2pz09orwjlhrt">Sync</MenuItem>
              <MenuItem value="Status Change" data-id="element-150" data-mp="qmo2k0qcisbm">Status Change</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{
          minWidth: 150
        }} data-id="element-151" data-mp="vkzdyfwahavy">
            <InputLabel data-id="element-152" data-mp="nlkvm0luvgoj">Source</InputLabel>
            <Select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} label="Source" data-id="element-153" data-mp="nmmid2c8hrm3">
              <MenuItem value="all" data-id="element-154" data-mp="xkk6zqy09qf8">All Sources</MenuItem>
              <MenuItem value="BO_USER" data-id="element-155" data-mp="sjcz7rvv4r8k">BO User</MenuItem>
              <MenuItem value="BANK_API" data-id="element-156" data-mp="sil56lhwz1ke">Bank API</MenuItem>
              <MenuItem value="SYSTEM" data-id="element-157" data-mp="16im2xs2vtsvn">System</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      overflowY: 'auto',
      height: 'calc(100vh - 280px)'
    }} data-id="element-158" data-mp="12hkc9zn16p5a">
        <TableContainer component={Paper} variant="outlined" sx={{
        boxShadow: 'none'
      }} data-id="element-159" data-mp="1gbl409fkfbw5">
          <Table size="small" data-id="element-160" data-mp="1q6v7l1lm3rc1">
            <TableHead data-id="element-161" data-mp="1q867gqbxsexv">
              <TableRow data-id="element-162" data-mp="1q5aap5btrwb0">
                <TableCell data-id="element-163" data-mp="1l8tkm5l2f7va">Timestamp</TableCell>
                <TableCell data-id="element-164" data-mp="1la4oe5eigfme">Event Type</TableCell>
                <TableCell data-id="element-165" data-mp="24umwt81ga0wr">Field Name</TableCell>
                <TableCell data-id="element-166" data-mp="24uvah25eda90">Old Value</TableCell>
                <TableCell data-id="element-167" data-mp="28vouvljms6x5">New Value</TableCell>
                <TableCell data-id="element-168" data-mp="28vf621abo5t1">Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody data-id="element-169" data-mp="1uve5yo1vt2em">
              {paginatedLogs.length === 0 ? <TableRow data-id="element-170" data-mp="1zuhb08hv38ml">
                  <TableCell colSpan={6} align="center" data-id="element-171" data-mp="1ztpcaosywus3">
                    <Box sx={{
                  py: 4
                }} data-id="element-172" data-mp="1l5ujz9ivgsfj">
                      <Icon icon="lucide:inbox" width={48} height={48} color="#94A3B8" data-id="element-173" data-mp="1pxw2hd6mvfe0" />
                      <Typography variant="body2" color="text.secondary" sx={{
                    mt: 2
                  }} data-id="element-174" data-mp="1h5amg4udzmsv">
                        No audit logs found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow> : paginatedLogs.map(log => <TableRow key={log.id} data-id="element-175" data-mp="1dnesklbholu9">
                    <TableCell data-id="element-176" data-mp="1dmd8xsgeqr0x">
                      <Typography variant="body2" fontSize="0.8rem" data-id="element-177" data-mp="18ottip7z487e">
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-178" data-mp="yz1xdqk8770t">
                      <Chip label={log.eventType} size="small" sx={{
                  backgroundColor: getEventTypeColor(log.eventType).bg,
                  color: getEventTypeColor(log.eventType).color,
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }} data-id="element-179" data-mp="yys6m0rcwvu2" />
                    </TableCell>
                    <TableCell data-id="element-180" data-mp="5w9592kjfzx7">
                      <Typography variant="body2" fontSize="0.8rem" fontWeight={500} data-id="element-181" data-mp="5x0tfsbt2vdg">
                        {log.fieldName}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-182" data-mp="ku1j7qafsg9a">
                      <Typography variant="body2" fontSize="0.8rem" color="text.secondary" data-id="element-183" data-mp="kszvomc8i1ao">
                        {log.oldValue || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-184" data-mp="3t7qnmzwptmez">
                      <Typography variant="body2" fontSize="0.8rem" fontWeight={500} data-id="element-185" data-mp="3t7gtr18juv40">
                        {log.newValue || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell data-id="element-186" data-mp="35hmmr0aww0ro">
                      <Chip label={log.source} size="small" sx={{
                  backgroundColor: getSourceColor(log.source).bg,
                  color: getSourceColor(log.source).color,
                  fontSize: '0.7rem'
                }} data-id="element-187" data-mp="36lnssximtihv" />
                    </TableCell>
                  </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{
      borderTop: '1px solid #E5E7EB',
      backgroundColor: 'background.paper'
    }} data-id="element-188" data-mp="2su97gk19557n">
        <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredLogs.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-189" data-mp="31m1fko2t0292" />
      </Box>
    </Drawer>;
};
export default AuditLogDrawer;